// Kompas Dnia — compass logic.
// Pure functions, no side effects. Easy to smoke-test from the console.

/**
 * balanceScore — a 0-100 index reflecting the day's overall balance.
 *
 * Formula (per spec):
 *   round((energy*20 + mood*20 + clarity*20 + planRealism*20 + (6 - pressure)*20) / 5)
 *
 * Ranges:
 *   All maxima (energy=5, mood=5, clarity=5, planRealism=5, pressure=1) → 100
 *   All minima (energy=1, mood=1, clarity=1, planRealism=1, pressure=5) → 20
 *   (Spec's second sample "24" appears to be a minor typo — the formula strictly
 *   yields 20; the primary sample 100 confirms the formula's shape.)
 */
export function balanceScore({ energy, mood, clarity, planRealism, pressure }) {
  const value =
    (energy * 20 +
      mood * 20 +
      clarity * 20 +
      planRealism * 20 +
      (6 - pressure) * 20) /
    5;
  return Math.round(value);
}

/**
 * calculateCompassResult — classifies the day into one of 5 directions.
 * The order of checks is critical and matches the spec exactly.
 *
 * Returns { result: 'act'|'rest'|'stay'|'change'|'pause', balanceScore: number }
 */
export function calculateCompassResult(answers) {
  const { energy, mood, pressure, clarity, planRealism, need } = answers;

  let result;

  if (
    (energy <= 2 && pressure >= 4) ||
    (mood <= 2 && clarity <= 2) ||
    need === "pause"
  ) {
    result = "pause";
  } else if (
    (energy <= 2 && pressure <= 3) ||
    (mood <= 2 && energy <= 3) ||
    need === "rest"
  ) {
    result = "rest";
  } else if (
    planRealism <= 2 ||
    (pressure >= 4 && clarity <= 3) ||
    need === "strategy"
  ) {
    result = "change";
  } else if (
    (energy >= 4 && clarity >= 4 && planRealism >= 3 && pressure <= 3) ||
    (need === "action" && energy >= 3 && clarity >= 3)
  ) {
    result = "act";
  } else if (
    (clarity >= 3 && planRealism >= 3 && energy >= 3) ||
    need === "direction"
  ) {
    result = "stay";
  } else {
    result = "stay"; // fallback
  }

  return { result, balanceScore: balanceScore(answers) };
}

/**
 * Deterministic pseudo-random index — same (seed, count) always returns the
 * same index. Used to pick a recommendation variant per (date, result) so that
 * the same day shows the same recommendation on reload.
 */
export function pickVariantIndex(seed, count) {
  if (!count || count <= 0) return 0;
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % count;
}

/* ------------------------------------------------------------------
 * Smoke tests — run from the browser console:
 *   import('/src/lib/compass.js').then(m => m.__runSmokeTests())
 *
 * Or just: window.__compassSmokeTests?.()
 * ------------------------------------------------------------------
 *
 * Test 1: energy=1, mood=3, pressure=5, clarity=3, planRealism=3, need=action
 *   → PAUSE (energy<=2 && pressure>=4)
 *
 * Test 2: energy=5, mood=4, pressure=2, clarity=5, planRealism=4, need=action
 *   → ACT
 *
 * Test 3: energy=3, mood=3, pressure=3, clarity=3, planRealism=1, need=direction
 *   → CHANGE (planRealism<=2)
 *
 * Test 4: energy=3, mood=3, pressure=3, clarity=3, planRealism=3, need=direction
 *   → STAY
 *
 * Test 5: energy=2, mood=2, pressure=2, clarity=4, planRealism=4, need=rest
 *   → REST
 *
 * balanceScore(max=5555,pressure=1) → 100
 * balanceScore(min=1111,pressure=5) → 20  (spec sample "24" is a typo)
 */
export function __runSmokeTests() {
  const cases = [
    {
      answers: { energy: 1, mood: 3, pressure: 5, clarity: 3, planRealism: 3, need: "action" },
      expected: "pause",
    },
    {
      answers: { energy: 5, mood: 4, pressure: 2, clarity: 5, planRealism: 4, need: "action" },
      expected: "act",
    },
    {
      answers: { energy: 3, mood: 3, pressure: 3, clarity: 3, planRealism: 1, need: "direction" },
      expected: "change",
    },
    {
      answers: { energy: 3, mood: 3, pressure: 3, clarity: 3, planRealism: 3, need: "direction" },
      expected: "stay",
    },
    {
      answers: { energy: 2, mood: 2, pressure: 2, clarity: 4, planRealism: 4, need: "rest" },
      expected: "rest",
    },
  ];
  const results = cases.map((c, i) => {
    const got = calculateCompassResult(c.answers).result;
    return { i, expected: c.expected, got, pass: got === c.expected };
  });
  const bMax = balanceScore({ energy: 5, mood: 5, clarity: 5, planRealism: 5, pressure: 1 });
  const bMin = balanceScore({ energy: 1, mood: 1, clarity: 1, planRealism: 1, pressure: 5 });
  return { results, balance: { max: bMax, min: bMin } };
}

if (typeof window !== "undefined") {
  window.__compassSmokeTests = __runSmokeTests;
}
